import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import NamespaceHeader from './components/NamespaceHeader';
import StringGroupsTable from './components/StringGroupsTable';
import CreateStringGroupModal from './components/CreateStringGroupModal';
import { useNamespacePageData } from './hooks/useNamespacePageData';
import { useStringGroupsData } from './hooks/useStringGroupsData';
import Skeleton from '@amzn/awsui-components-console/skeleton';
import StackedBreadcrumbGroup from 'src/common/ui/StackedBreadcrumbGroup';
import { CreateStringGroupFormData } from './types';

const NamespaceDetailPage: React.FC = () => {
  const { namespaceId } = useParams<{ namespaceId: string }>();
  const pageData = useNamespacePageData(namespaceId);
  const stringGroupsData = useStringGroupsData(namespaceId);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const openCreateModal = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleCreateStringGroup = useCallback(
    async (_data: CreateStringGroupFormData): Promise<void> => {
      // TODO: Wire to GraphQL mutation (e.g. useCreateStringGroupMutation)
      // await createStringGroupMutation({ variables: { input: { ... } } });
      setShowSuccessAlert(true);
    },
    [],
  );

  return (
    <SpaceBetween size="l">
      <StackedBreadcrumbGroup
        items={[{ topText: 'NAMESPACE', bottomText: namespaceId ?? '' }]}
      />

      {showSuccessAlert && (
        <Alert
          type="success"
          dismissible
          dismissAriaLabel="Dismiss"
          onDismiss={() => setShowSuccessAlert(false)}
        >
          String group created successfully.
        </Alert>
      )}

      {pageData.namespaceError ? (
        <Alert type="error" header="Failed to load namespace">
          {pageData.namespaceError.message}
        </Alert>
      ) : pageData.namespaceLoading ? (
        <Box textAlign="center" padding="l">
          <Skeleton />
        </Box>
      ) : (
        <NamespaceHeader
          namespace={pageData.namespace}
          onCreateStringGroup={openCreateModal}
        />
      )}

      {stringGroupsData.error && (
        <Alert type="error" header="Failed to load string groups">
          {stringGroupsData.error.message}
        </Alert>
      )}

      {!pageData.namespaceError && !stringGroupsData.error && (
        <StringGroupsTable
          displayItems={stringGroupsData.displayItems}
          loading={stringGroupsData.initialLoading}
          isLoadingMore={stringGroupsData.isLoadingMore}
          hasMore={stringGroupsData.hasMore}
          handleLoadMore={stringGroupsData.handleLoadMore}
          searchText={stringGroupsData.searchText}
          onSearchChange={stringGroupsData.onSearchChange}
          hideCxFamilyColumn={!!pageData.namespace?.defaultCxFamily}
          onCreateStringGroup={openCreateModal}
        />
      )}

      <CreateStringGroupModal
        visible={showCreateModal}
        onDismiss={() => setShowCreateModal(false)}
        onSubmit={handleCreateStringGroup}
        cxFamily={pageData.namespace?.defaultCxFamily}
      />
    </SpaceBetween>
  );
};

export default NamespaceDetailPage;
